<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

import type { CheckResponse, OperatorRecord, RegistryResult } from '../shared/contracts'
import { INN_VALIDATION_MESSAGE, isValidInn } from '../shared/inn'

type ViewState = 'idle' | 'loading' | 'result' | 'unavailable' | 'request-error'

const inn = ref('')
const validationError = ref('')
const state = ref<ViewState>('idle')
const result = ref<RegistryResult | null>(null)
const shareMessage = ref('')
let requestSequence = 0

const found = computed(() => result.value?.status === 'found')
const records = computed<OperatorRecord[]>(() => result.value?.records ?? [])

function digitsOnly(value: string): string {
  return value.replace(/\D/g, '').slice(0, 12)
}

function updateInn(event: Event): void {
  inn.value = digitsOnly((event.target as HTMLInputElement).value)
  validationError.value = ''
}

function resultPath(value: string): string {
  return `/check/${encodeURIComponent(value)}`
}

function updateDocumentMetadata(value: string): void {
  const title = `Проверка ИНН ${value} в реестре операторов РКН`
  const description = `Проверьте, есть ли организация с ИНН ${value} в реестре операторов персональных данных Роскомнадзора.`
  document.title = title
  document.querySelector('meta[name="description"]')?.setAttribute('content', description)
  document.querySelector('meta[property="og:title"]')?.setAttribute('content', title)
  document.querySelector('meta[property="og:description"]')?.setAttribute('content', description)
}

async function runCheck(value: string, changeAddress: boolean): Promise<void> {
  validationError.value = ''
  if (!isValidInn(value)) {
    validationError.value = INN_VALIDATION_MESSAGE
    state.value = 'idle'
    result.value = null
    return
  }

  if (changeAddress && window.location.pathname !== resultPath(value)) {
    window.history.pushState({}, '', resultPath(value))
  }
  updateDocumentMetadata(value)
  const sequence = ++requestSequence
  state.value = 'loading'
  result.value = null

  try {
    const response = await fetch(`/api/check?inn=${encodeURIComponent(value)}`, {
      headers: { accept: 'application/json' },
    })
    const payload = await response.json() as CheckResponse | { error?: string }
    if (sequence !== requestSequence) return
    if (!response.ok) {
      validationError.value = 'error' in payload && payload.error
        ? payload.error
        : 'Не удалось выполнить проверку.'
      state.value = 'idle'
      return
    }
    if (!('status' in payload) || payload.status === 'unavailable') {
      state.value = 'unavailable'
      return
    }
    result.value = payload
    state.value = 'result'
  } catch {
    if (sequence === requestSequence) state.value = 'request-error'
  }
}

function submit(): void {
  void runCheck(inn.value, true)
}

async function shareResult(): Promise<void> {
  const shareData = {
    title: document.title,
    text: found.value
      ? `Организация с ИНН ${inn.value} найдена в реестре операторов персональных данных.`
      : `Результат проверки ИНН ${inn.value} в реестре операторов персональных данных.`,
    url: window.location.href,
  }
  try {
    if (navigator.share) {
      await navigator.share(shareData)
      return
    }
    await navigator.clipboard.writeText(window.location.href)
    shareMessage.value = 'Ссылка скопирована'
    window.setTimeout(() => { shareMessage.value = '' }, 2_000)
  } catch {
    // Отмена системного диалога не требует сообщения об ошибке.
  }
}

function readPath(): void {
  const match = window.location.pathname.match(/^\/check\/(\d{10}|\d{12})\/?$/)
  if (!match?.[1]) {
    requestSequence += 1
    state.value = 'idle'
    result.value = null
    return
  }
  inn.value = match[1]
  void runCheck(inn.value, false)
}

onMounted(() => {
  window.addEventListener('popstate', readPath)
  readPath()
})

onBeforeUnmount(() => {
  window.removeEventListener('popstate', readPath)
})
</script>

<template>
  <div class="page-shell">
    <header class="site-header">
      <a class="brand" href="/" aria-label="На главную">
        <span class="brand-mark" aria-hidden="true">
          <svg viewBox="0 0 28 28">
            <path d="M14 2.5 24 6.7v6.8c0 6.2-4.2 10.1-10 12-5.8-1.9-10-5.8-10-12V6.7L14 2.5Z" />
            <path d="m9.3 14 3 3.1 6.7-7" />
          </svg>
        </span>
        <span>Проверка реестра РКН</span>
      </a>
      <a class="header-link" href="#documents">Документы по 152-ФЗ</a>
    </header>

    <main>
      <section class="hero" aria-labelledby="page-title">
        <div class="hero-copy">
          <p class="eyebrow">Открытые данные Роскомнадзора</p>
          <h1 id="page-title">Проверьте организацию в реестре операторов персональных данных</h1>
          <p class="lead">
            Введите ИНН — мы найдём сведения в официальном реестре РКН и покажем результат.
            Проверка бесплатная.
          </p>
        </div>

        <div class="checker-card">
          <form class="check-form" novalidate @submit.prevent="submit">
            <label for="inn">ИНН организации или ИП</label>
            <div class="input-row">
              <input
                id="inn"
                :value="inn"
                type="text"
                inputmode="numeric"
                autocomplete="off"
                maxlength="12"
                placeholder="10 или 12 цифр"
                aria-describedby="inn-help inn-error"
                :aria-invalid="Boolean(validationError)"
                @input="updateInn"
              >
              <button class="primary-button" type="submit" :disabled="state === 'loading'">
                <span v-if="state === 'loading'" class="spinner" aria-hidden="true"></span>
                {{ state === 'loading' ? 'Проверяем' : 'Проверить' }}
              </button>
            </div>
            <p id="inn-help" class="field-help">Для юридического лица — 10 цифр, для ИП — 12.</p>
            <p v-if="validationError" id="inn-error" class="field-error" role="alert">{{ validationError }}</p>
          </form>

          <div v-if="state === 'loading'" class="loading-panel" aria-live="polite">
            <span class="loading-orbit" aria-hidden="true"></span>
            <div>
              <strong>Обращаемся к реестру</strong>
              <p>Обычно это занимает несколько секунд.</p>
            </div>
          </div>

          <section
            v-else-if="state === 'result' && result"
            class="result-card"
            :class="found ? 'result-found' : 'result-missing'"
            aria-live="polite"
          >
            <div class="result-heading">
              <span class="result-icon" aria-hidden="true">
                <svg v-if="found" viewBox="0 0 24 24"><path d="m5 12.5 4.2 4.2L19.5 6.5" /></svg>
                <svg v-else viewBox="0 0 24 24"><path d="M12 8v5.2M12 17.2v.1" /><circle cx="12" cy="12" r="9" /></svg>
              </span>
              <div>
                <p class="result-kicker">Результат проверки ИНН {{ result.inn }}</p>
                <h2>{{ found ? 'Организация в реестре' : 'Запись в реестре не найдена' }}</h2>
              </div>
            </div>

            <p v-if="result.stale" class="stale-note">
              Реестр РКН сейчас недоступен. Показан последний сохранённый результат от
              {{ new Date(result.cachedAt).toLocaleDateString('ru-RU') }} — он мог устареть.
            </p>

            <template v-if="found">
              <div v-for="record in records" :key="record.registrationNumber" class="operator-record">
                <h3>{{ record.name }}</h3>
                <dl>
                  <div>
                    <dt>Регистрационный номер</dt>
                    <dd>{{ record.registrationNumber }}</dd>
                  </div>
                  <div>
                    <dt>Дата регистрации уведомления</dt>
                    <dd>{{ record.notificationRegisteredAt }}</dd>
                  </div>
                </dl>
              </div>
              <p class="calm-status">
                <span aria-hidden="true">✓</span>
                Уведомление об обработке персональных данных подано.
              </p>
              <p class="result-explanation">
                Подача уведомления — не единственная обязанность оператора. С 1 сентября 2025 года
                согласие на обработку персональных данных оформляется отдельным документом, а на
                сайте должна быть размещена политика обработки персональных данных. Если сведения
                менялись — адрес, ответственный, цели обработки, — в Роскомнадзор подаётся
                уведомление об изменении сведений.
              </p>
            </template>

            <template v-else>
              <p class="result-explanation">
                По этому ИНН в открытом реестре не найдено поданного уведомления. Если на сайте есть
                форма обратной связи, онлайн-запись, корзина с оформлением заказа или подписка на
                рассылку — персональные данные обрабатываются, и уведомить Роскомнадзор нужно до
                начала обработки.
              </p>
              <div class="penalty-note">
                <strong>Ответственность за неподачу уведомления</strong>
                <p>
                  Часть 10 статьи 13.11 КоАП РФ предусматривает штраф: для должностных лиц —
                  30&nbsp;000–50&nbsp;000 ₽, для ИП и юридических лиц — 100&nbsp;000–300&nbsp;000 ₽.
                  Размеры действуют с 30 мая 2025 года.
                </p>
              </div>
              <p class="result-explanation">
                Само уведомление подаётся бесплатно. Платить имеет смысл только за подготовку
                документов, если разбираться самостоятельно не хочется.
              </p>
              <a class="primary-button result-cta" href="#documents">Подготовить документы за 1490 ₽</a>
              <p class="fine-print">
                Отсутствие записи может означать и то, что уведомление подано недавно и ещё не
                отражено в реестре, либо что сведения внесены по другому ИНН. Проверьте данные,
                прежде чем принимать решение.
              </p>
            </template>

            <button class="share-button" type="button" @click="shareResult">
              <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="18" cy="5" r="2.5" /><circle cx="6" cy="12" r="2.5" /><circle cx="18" cy="19" r="2.5" /><path d="m8.2 10.8 7.6-4.5M8.2 13.2l7.6 4.5" /></svg>
              {{ shareMessage || 'Поделиться результатом' }}
            </button>
          </section>

          <div v-else-if="state === 'unavailable' || state === 'request-error'" class="unavailable-card" role="alert">
            <span class="result-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24"><path d="M12 8v5.2M12 17.2v.1" /><circle cx="12" cy="12" r="9" /></svg>
            </span>
            <div>
              <h2>Сервис реестра сейчас недоступен</h2>
              <p>Не удалось получить достоверный ответ. Попробуйте проверить ИНН немного позже.</p>
            </div>
          </div>

          <p class="source-note">
            Источник:
            <a href="https://pd.rkn.gov.ru/operators-registry/operators-list/" target="_blank" rel="noreferrer">
              реестр операторов Роскомнадзора
            </a>.
            Данные могут обновляться с задержкой.
          </p>
        </div>
      </section>

      <section id="documents" class="offer-section" aria-labelledby="offer-title">
        <div class="offer-heading">
          <p class="eyebrow">Следующий шаг</p>
          <h2 id="offer-title">Пакет документов по 152-ФЗ для вашей организации</h2>
          <p>
            Заполните данные один раз и получите редактируемый комплект для уведомления
            Роскомнадзора и размещения на сайте.
          </p>
        </div>

        <div class="offer-grid">
          <div class="package-card">
            <h3>Что входит в пакет</h3>
            <ul>
              <li>Уведомление в Роскомнадзор — по форме приказа РКН от 28.10.2022 № 180</li>
              <li>Политика обработки персональных данных для сайта</li>
              <li>Форма согласия — отдельным документом, как требуется с 1 сентября 2025 года</li>
              <li>Отдельное согласие на рекламные рассылки, если они есть</li>
              <li>Пошаговая инструкция подачи со ссылками на формы</li>
              <li>Памятка: что и куда разместить на сайте</li>
            </ul>
            <p class="package-note">Форматы: Word и PDF. Обычно занимает 10 минут — 7 вопросов о вашем сайте.</p>
          </div>

          <aside class="price-card">
            <p class="price-label">Весь комплект</p>
            <p class="price">1 490 <span>₽</span></p>
            <button class="buy-button" type="button" disabled>
              Купить
              <span>скоро</span>
            </button>
            <p class="honest-note">
              Мы не оказываем юридические услуги и не консультируем. Это конструктор документов:
              он подставляет ваши данные в выверенные шаблоны. Проверьте готовые документы перед
              подачей — за достоверность введённых сведений отвечаете вы.
            </p>
            <p class="honest-note">
              Подать уведомление нужно самостоятельно: на бумаге, электронно с квалифицированной
              электронной подписью или через Госуслуги. Мы готовим документы, но не подаём их за вас
              и не представляем ваши интересы в Роскомнадзоре.
            </p>
          </aside>
        </div>
      </section>

      <section class="faq-section" aria-labelledby="faq-title">
        <h2 id="faq-title">Частые вопросы</h2>
        <div class="faq-list">
          <details>
            <summary>Кто считается оператором персональных данных?</summary>
            <p>
              Практически любой владелец сайта, который собирает данные людей: имя и телефон в форме
              обратной связи, email для рассылки, данные покупателя при оформлении заказа, запись на
              приём. Оператором становится и тот, кто обрабатывает данные своих работников.
            </p>
          </details>
          <details>
            <summary>Подача уведомления платная?</summary>
            <p>
              Нет. Подача уведомления в Роскомнадзор бесплатна. Платить имеет смысл только за
              подготовку документов, если разбираться самостоятельно не хочется.
            </p>
          </details>
          <details>
            <summary>Как подаётся уведомление?</summary>
            <p>
              Тремя способами: на бумаге, электронно с усиленной квалифицированной электронной
              подписью либо электронно через Госуслуги. Форма уведомления утверждена приказом
              Роскомнадзора от 28.10.2022 № 180.
            </p>
          </details>
          <details>
            <summary>Вы гарантируете, что штрафа не будет?</summary>
            <p>
              Нет, и никто не может этого гарантировать. Мы готовим документы по вашим данным;
              решение о соответствии закону принимает надзорный орган.
            </p>
          </details>
          <details>
            <summary>Что вы делаете с моими данными?</summary>
            <p>
              Мы сами оператор персональных данных и храним минимум: сырые IP-адреса не сохраняем,
              сведения, введённые для подготовки документов, удаляем через 30 дней после выдачи
              заказа. Подробности — в <a href="/privacy">политике обработки персональных данных</a>.
            </p>
          </details>
        </div>
      </section>
    </main>

    <footer>
      <p>Сервис использует общедоступные сведения официального реестра Роскомнадзора.</p>
      <p>Не является сайтом государственного органа.</p>
      <p class="footer-legal">
        ИП Созыкин Алексей Сергеевич, ИНН 246517814287, ОГРНИП 326246800042402 ·
        <a href="mailto:xkuragax@gmail.com">xkuragax@gmail.com</a> ·
        <a href="/privacy">Политика обработки персональных данных</a>
      </p>
    </footer>
  </div>
</template>
